import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '@components/common/Icon';
import { SITE_INFO, NAV_LINKS } from '@lib/constants';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isDark, setIsDark] = useState(() => {
    return document.documentElement.classList.contains('dark');
  });
  const dropdownRef = useRef(null);
  const location = useLocation();

  // Portfolio section links (Academics through Gallery)
  const portfolioLinks = NAV_LINKS.slice(2, 10); // Academics to Gallery

  // Quick links (Home, About)
  const quickLinks = [NAV_LINKS[0], NAV_LINKS[1]];

  // Footer links (Contact)
  const contactLink = NAV_LINKS[10];

  // Get current page label for dropdown button
  const currentPage = NAV_LINKS.find(link => link.path === location.pathname);
  const isPortfolioPage = portfolioLinks.some(link => link.path === location.pathname);

  useEffect(() => {
    const isDarkMode = document.documentElement.classList.contains('dark');
    setIsDark(isDarkMode);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleDarkMode = () => {
    const html = document.documentElement;
    const willBeDark = !isDark;

    if (willBeDark) {
      html.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      html.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
    setIsDark(willBeDark);
  };

  return (
    <header
      className="sticky top-0 z-50 w-full border-b backdrop-blur"
      style={{
        backgroundColor: 'var(--color-header-bg)',
        borderColor: 'var(--color-header-border)'
      }}
    >
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          {/* Logo/Name */}
          <Link
            to="/"
            className="flex items-center space-x-2 text-lg sm:text-xl font-bold font-heading transition-colors duration-200 whitespace-nowrap"
            style={{ color: 'var(--color-header-text)' }}
            onMouseEnter={(e) => e.currentTarget.style.color = 'var(--color-link-hover)'}
            onMouseLeave={(e) => e.currentTarget.style.color = 'var(--color-header-text)'}
          >
            <span className="hidden sm:inline">{SITE_INFO.name}</span>
            <span className="sm:hidden">S. Poddar</span>
          </Link>

          {/* Desktop Navigation - Compact Version */}
          <div className="hidden md:flex items-center space-x-2">
            {/* Home Link */}
            <Link
              to="/"
              className="px-3 py-2 rounded-lg transition-colors text-sm font-medium"
              style={{
                backgroundColor: location.pathname === '/' ? 'var(--color-theme-2)' : 'transparent',
                color: location.pathname === '/' ? 'var(--color-heading)' : 'var(--color-muted)',
              }}
              onMouseEnter={(e) => {
                if (location.pathname !== '/') {
                  e.currentTarget.style.backgroundColor = 'var(--color-theme-2)';
                }
              }}
              onMouseLeave={(e) => {
                if (location.pathname !== '/') {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
            >
              Home
            </Link>

            {/* About Link */}
            <Link
              to="/about"
              className="px-3 py-2 rounded-lg transition-colors text-sm font-medium"
              style={{
                backgroundColor: location.pathname === '/about' ? 'var(--color-theme-2)' : 'transparent',
                color: location.pathname === '/about' ? 'var(--color-heading)' : 'var(--color-muted)',
              }}
              onMouseEnter={(e) => {
                if (location.pathname !== '/about') {
                  e.currentTarget.style.backgroundColor = 'var(--color-theme-2)';
                }
              }}
              onMouseLeave={(e) => {
                if (location.pathname !== '/about') {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
            >
              About
            </Link>

            {/* Portfolio Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="px-3 py-2 rounded-lg transition-colors text-sm font-medium flex items-center gap-1"
                style={{
                  backgroundColor: isPortfolioPage ? 'var(--color-theme-2)' : 'transparent',
                  color: isPortfolioPage ? 'var(--color-heading)' : 'var(--color-muted)',
                }}
                onMouseEnter={(e) => {
                  if (!isPortfolioPage) {
                    e.currentTarget.style.backgroundColor = 'var(--color-theme-2)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isPortfolioPage) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                <span>{isPortfolioPage ? currentPage?.label : 'Portfolio'}</span>
                <svg
                  className={`w-4 h-4 transition-transform duration-300 ${dropdownOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                </svg>
              </button>

              {/* Dropdown Menu */}
              {dropdownOpen && (
                <div
                  className="absolute top-full right-0 mt-1 rounded-lg shadow-lg border backdrop-blur-sm"
                  style={{
                    backgroundColor: 'var(--color-card-bg)',
                    borderColor: 'var(--color-card-border)',
                    minWidth: '200px',
                  }}
                >
                  {portfolioLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      className="block px-4 py-2 text-sm transition-colors first:rounded-t-lg last:rounded-b-lg"
                      style={{
                        backgroundColor:
                          location.pathname === link.path ? 'var(--color-theme-2)' : 'transparent',
                        color:
                          location.pathname === link.path
                            ? 'var(--color-heading)'
                            : 'var(--color-muted)',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'var(--color-theme-2)';
                        e.currentTarget.style.color = 'var(--color-heading)';
                      }}
                      onMouseLeave={(e) => {
                        if (location.pathname !== link.path) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.color = 'var(--color-muted)';
                        }
                      }}
                      onClick={() => setDropdownOpen(false)}
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Contact Link */}
            <Link
              to="/contact"
              className="px-3 py-2 rounded-lg transition-colors text-sm font-medium"
              style={{
                backgroundColor: location.pathname === '/contact' ? 'var(--color-theme-2)' : 'transparent',
                color: location.pathname === '/contact' ? 'var(--color-heading)' : 'var(--color-muted)',
              }}
              onMouseEnter={(e) => {
                if (location.pathname !== '/contact') {
                  e.currentTarget.style.backgroundColor = 'var(--color-theme-2)';
                }
              }}
              onMouseLeave={(e) => {
                if (location.pathname !== '/contact') {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
            >
              Contact
            </Link>

            {/* Dark Mode Toggle */}
            <button
              type="button"
              onClick={toggleDarkMode}
              className="px-4 py-2 rounded-lg transition-colors duration-200 cursor-pointer ml-2"
              style={{ color: 'var(--color-muted)' }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--color-theme-2)')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
              aria-label="Toggle dark mode"
              title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              <Icon name={isDark ? 'sun' : 'moon'} size="md" />
            </button>
          </div>

          {/* Mobile Menu Controls */}
          <div className="md:hidden flex items-center space-x-2">
            {/* Mobile Dark Mode Toggle */}
            <button
              type="button"
              onClick={toggleDarkMode}
              className="px-3 py-2 rounded-lg transition-colors duration-200 cursor-pointer"
              style={{ color: 'var(--color-muted)' }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--color-theme-2)')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
              aria-label="Toggle dark mode"
            >
              <Icon name={isDark ? 'sun' : 'moon'} size="md" />
            </button>

            {/* Mobile Menu Button */}
            <button
              className="p-2 rounded-lg transition-colors duration-200"
              style={{ color: 'var(--color-muted)' }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--color-theme-2)')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle mobile menu"
            >
              {mobileMenuOpen ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div
            className="md:hidden py-4 border-t space-y-2"
            style={{ borderColor: 'var(--color-header-border)' }}
          >
            {NAV_LINKS.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="block px-4 py-2 rounded-lg transition-colors"
                style={{
                  backgroundColor:
                    location.pathname === link.path ? 'var(--color-theme-2)' : 'transparent',
                  color:
                    location.pathname === link.path
                      ? 'var(--color-heading)'
                      : 'var(--color-muted)',
                }}
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.label}
              </Link>
            ))}
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
